/* ============================================================
   storage — home.

   What you land on: the things you came to do, and the things
   worth knowing, each one a way in rather than a number to read.

     • put something somewhere (upload to a drive, a new folder),
       or look through everything (search lives in the header)
     • what was added lately — by anyone, or just yours — with
       "see all" opening search, newest first
     • what you opened lately, from where you left off
     • anything moving: transfers and trims
     • each drive on one line: how full, whether it is there
     • the trash, and the public links you have out

   Nothing here is shown for its own sake: a section with nothing
   in it says so in one line or is not there, and every row opens
   something.
   ============================================================ */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { Icon, MarkerBadge, fileIconName, fmtBytes, fmtDate } from './lib';
import { NameModal } from './pickers';
import { savedPosition, fmtTime } from './MediaPlayer';

const OPENED_KEY = 'st.home.opened';
const OPENED_MAX = 12;

/** Remember a file someone opened, for "recently opened" on Home. Only here,
 *  in this browser: it is a convenience, not a record. */
export function rememberOpened(file) {
  if (!file?.id) return;
  try {
    const list = JSON.parse(window.localStorage.getItem(OPENED_KEY) || '[]').filter((x) => x.id !== file.id);
    list.unshift({
      id: file.id, name: file.original_name, drive: file.drive, path: file.path,
      mime: file.mime_type, size: file.size, at: Date.now(),
    });
    window.localStorage.setItem(OPENED_KEY, JSON.stringify(list.slice(0, OPENED_MAX)));
  } catch { /* private mode: nothing remembered */ }
}

function readOpened() {
  try { return JSON.parse(window.localStorage.getItem(OPENED_KEY) || '[]'); } catch { return []; }
}

function forgetOpened(id) {
  try {
    const list = readOpened().filter((x) => x.id !== id);
    window.localStorage.setItem(OPENED_KEY, JSON.stringify(list));
  } catch { /* nothing to forget */ }
}

const ago = (ts) => {
  const s = Math.max(0, Date.now() / 1000 - ts);
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return fmtDate(ts);
};

function Panel({ title, action, children, className = '' }) {
  return (
    <section className={`panel st-home-panel ${className}`}>
      <div className="panel-head st-home-phead">
        <span className="label">{title}</span>
        {action && <div className="st-home-pacts">{action}</div>}
      </div>
      {children}
    </section>
  );
}

function SkelRows({ n = 4 }) {
  return (
    <ul className="st-home-list" aria-hidden="true">
      {[...Array(n)].map((_, i) => (
        <li key={i} className="st-home-row st-home-row--skel">
          <span className="skeleton st-sk st-sk--w70"/>
          <span className="skeleton st-sk st-sk--short"/>
        </li>
      ))}
    </ul>
  );
}

/* One row: an icon, a name that is the point, a quieter line under it, and
   something short at the end. Every row opens something. */
function Row({ icon, name, sub, end, endWarn, onClick, title, disabled, children }) {
  return (
    <li>
      <button type="button" className="st-home-row" onClick={onClick} title={title} disabled={disabled}>
        {icon}
        <span className="st-home-name">{name}</span>
        {sub && <span className="st-home-sub">{sub}</span>}
        {end != null && <span className={`st-home-end tnum ${endWarn ? 'is-warn' : ''}`}>{end}</span>}
        {children}
      </button>
    </li>
  );
}

const RECENT_ROWS = 10;

export default function Home({
  token, me, drives, quota, drivesLoading, transfers, trim, toast,
  onOpenDrive, onOpenFile, onOpenFolder, onSearch, onUpload, onShowTransfers, goView, onChanged,
}) {
  const [recent, setRecent] = useState(null);
  const [mineOnly, setMineOnly] = useState(false);
  const [shares, setShares] = useState(null);
  const [opened, setOpened] = useState(readOpened);
  const [target, setTarget] = useState(null);         // the drive Upload sends to
  const [naming, setNaming] = useState(false);
  const fileInput = useRef(null);

  const visible = useMemo(() => drives.filter((d) => d.perms?.see !== false), [drives]);
  const writable = useMemo(() => visible.filter((d) => d.online && d.perms?.upload !== false), [visible]);
  useEffect(() => {
    if (!target || !writable.some((d) => d.id === target)) setTarget(writable[0]?.id || null);
  }, [writable, target]);

  // Recently added: every drive this person may browse, newest first.
  const driveKey = visible.filter((d) => d.online && d.perms?.browse !== false).map((d) => d.id).join(',');
  useEffect(() => {
    if (!driveKey) { if (!drivesLoading) setRecent([]); return undefined; }
    let live = true;
    Promise.all(driveKey.split(',').map((id) => api.allFiles(token, id).catch(() => [])))
      .then((lists) => {
        if (live) setRecent(lists.flat().sort((a, b) => b.created_at - a.created_at).slice(0, 80));
      });
    return () => { live = false; };
  }, [token, driveKey, drivesLoading]);

  useEffect(() => {
    let live = true;
    api.sharesMine(token).then((x) => { if (live) setShares(x); }).catch(() => { if (live) setShares([]); });
    return () => { live = false; };
  }, [token]);

  const label = (id) => drives.find((d) => d.id === id)?.label || id;
  const rows = (recent || []).filter((f) => !mineOnly || f.owner_id === me?.id);
  const mine = (shares || []).filter((x) => x.owner_id === me?.id);
  const soon = mine.filter((x) => x.expires_at && x.expires_at - Date.now() / 1000 < 3 * 86400);
  const moving = [
    ...transfers.active.map((t) => ({ kind: 't', t })),
    ...(trim?.jobs || []).filter((j) => ['queued', 'working', 'committing', 'ready'].includes(j.status)).map((j) => ({ kind: 'j', j })),
  ];
  const tr = quota?.trash;

  return (
    <div className="st-home">
      {/* What people come to do. Search is the header's; one box, not two. */}
      <div className="st-home-bar" role="toolbar" aria-label="Quick actions">
        <div className="st-home-up">
          <button type="button" className="btn btn--sm st-home-upbtn" onClick={() => fileInput.current?.click()} disabled={!target}
                  title={target ? `Upload files to the top of ${label(target)}` : 'No drive you may upload to'}>
            <Icon name="upload" size={14}/> Upload
          </button>
          {writable.length > 1 ? (
            <select className="select st-home-to" value={target || ''} onChange={(e) => setTarget(e.target.value)}
                    aria-label="Upload to which drive">
              {writable.map((d) => <option key={d.id} value={d.id}>to {d.label}</option>)}
            </select>
          ) : target && <span className="st-home-tolabel">to {label(target)}</span>}
          <input ref={fileInput} type="file" multiple hidden
                 onChange={(e) => {
                   const files = Array.from(e.target.files || []);
                   e.target.value = '';
                   if (files.length && target) onUpload(files.map((file) => ({ file, relPath: file.name })), { drive: target, path: '/' });
                 }}/>
        </div>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setNaming(true)} disabled={!target}>
          <Icon name="plus" size={14}/> New folder
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => onSearch('', 'added')}>
          <Icon name="search" size={14}/> Everything
        </button>
      </div>

      <div className="st-home-cols">
        <div className="st-home-main">
          <Panel title="Recently added" action={
            <>
              <div className="st-home-seg" role="group" aria-label="Whose files">
                <button type="button" className={`st-chip ${!mineOnly ? 'on' : ''}`} aria-pressed={!mineOnly} onClick={() => setMineOnly(false)}>Everyone</button>
                <button type="button" className={`st-chip ${mineOnly ? 'on' : ''}`} aria-pressed={mineOnly} onClick={() => setMineOnly(true)}>Yours</button>
              </div>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => onSearch('', 'added', mineOnly ? me?.id : '')}>See all</button>
            </>
          }>
            {recent === null ? <SkelRows n={8}/> : rows.length === 0 ? (
              <p className="st-home-quiet">{mineOnly ? 'Nothing of yours yet.' : 'Nothing has been added yet.'}</p>
            ) : (
              <ul className="st-home-list">
                {rows.slice(0, RECENT_ROWS).map((f) => (
                  <Row key={f.id} onClick={() => onOpenFile(f)} title={`${f.original_name}\n${label(f.drive)} ${f.path}`}
                       icon={<Icon name={fileIconName(f.mime_type, f.original_name)} size={16} className="st-icon-file"/>}
                       name={f.original_name}
                       sub={<>{f.markers > 0 && <><MarkerBadge count={f.markers} size={11}/>{' '}</>}{`${f.owner_id === me?.id ? 'you' : f.owner_name} · ${label(f.drive)}${f.path !== '/' ? ` ${f.path}` : ''}`}</>}
                       end={ago(f.created_at)}/>
                ))}
              </ul>
            )}
            {rows.length > RECENT_ROWS && (
              <button type="button" className="st-home-more" onClick={() => onSearch('', 'added', mineOnly ? me?.id : '')}>
                {rows.length >= 80 ? 'More' : `${rows.length - RECENT_ROWS} more`}, newest first
              </button>
            )}
          </Panel>

          {opened.length > 0 && (
            <Panel title="Opened lately" action={
              <button type="button" className="btn btn--ghost btn--sm"
                      onClick={() => { try { window.localStorage.removeItem(OPENED_KEY); } catch { /* */ } setOpened([]); }}>Clear</button>
            }>
              <ul className="st-home-list">
                {opened.slice(0, 6).map((o) => {
                  const at = savedPosition(o.id);
                  return (
                    <Row key={o.id} title={o.name}
                         onClick={() => onOpenFile({ id: o.id, drive: o.drive, path: o.path }, at, () => { forgetOpened(o.id); setOpened(readOpened()); })}
                         icon={<Icon name={fileIconName(o.mime, o.name)} size={16} className="st-icon-file"/>}
                         name={o.name}
                         sub={at > 0 ? `resume at ${fmtTime(at)}` : `${label(o.drive)}${o.path && o.path !== '/' ? ` ${o.path}` : ''}`}
                         end={ago(o.at / 1000)}/>
                  );
                })}
              </ul>
            </Panel>
          )}
        </div>

        <aside className="st-home-side">
          {moving.length > 0 && (
            <Panel title="On the way" action={
              <button type="button" className="btn btn--ghost btn--sm" onClick={onShowTransfers}>Transfers</button>
            }>
              <ul className="st-home-list">
                {moving.slice(0, 5).map((m) => (m.kind === 't' ? (
                  <Row key={m.t.id} onClick={onShowTransfers} title={m.t.name}
                       icon={<Icon name={m.t.kind === 'upload' ? 'upload' : 'download'} size={16}/>}
                       name={m.t.name} sub={m.t.route === 'direct' ? `${m.t.kind} · direct · LAN` : `${m.t.kind} · ${m.t.state}`}
                       end={m.t.size ? `${Math.round(((m.t.sent || 0) / m.t.size) * 100)}%` : ''}/>
                ) : (
                  <Row key={m.j.id} onClick={() => trim.onOpenFile?.(m.j)} title={m.j.file_current_name || m.j.file_name}
                       icon={<Icon name="cut" size={16}/>}
                       name={m.j.file_current_name || m.j.file_name}
                       sub={m.j.status === 'ready' ? 'trim · ready to save' : `trim · ${m.j.status}`}
                       end={m.j.status === 'working' ? `${Math.round((m.j.progress || 0) * 100)}%` : ''}/>
                )))}
              </ul>
              {moving.length > 5 && (
                <button type="button" className="st-home-more" onClick={onShowTransfers}>{moving.length - 5} more</button>
              )}
            </Panel>
          )}

          <Panel title="Drives" action={
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => goView('dashboard')}>Details</button>
          }>
            {drivesLoading && !drives.length ? <SkelRows n={3}/> : (
              <ul className="st-home-list">
                {visible.map((d) => {
                  const pct = d.cap ? Math.min(100, (d.used / d.cap) * 100) : 0;
                  const state = d.setup_needed ? 'not set up yet' : !d.online ? 'offline'
                    : `${fmtBytes(d.used)} / ${fmtBytes(d.cap).replace(/\.00 /, ' ')}`;
                  return (
                    <li key={d.id}>
                      <button type="button" className={`st-home-row st-home-drive ${d.online ? '' : 'is-off'}`}
                              onClick={() => (d.online ? onOpenDrive(d.id) : goView('dashboard'))}
                              title={d.online ? `Open ${d.label}` : `${d.label}: ${state}`}>
                        <span className={`dot ${d.online ? 'dot--ok' : 'dot--err'}`} aria-hidden="true"/>
                        <span className="st-home-name">{d.label}</span>
                        <span className="st-home-end tnum">{state}</span>
                        <span className="bar st-home-bar" aria-hidden="true">
                          <span className={`fill ${pct > 90 ? 'fill--err' : pct > 75 ? 'fill--warn' : ''}`}
                                style={{ width: `${d.online ? Math.max(pct, 0.5) : 0}%` }}/>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>

          {/* The rest, one quiet line each. */}
          <Panel title="Trash and links">
            <ul className="st-home-list">
              <Row onClick={() => goView('trash')} icon={<Icon name="trash" size={16}/>}
                   name="Trash"
                   sub={tr ? `cleared after ${tr.days} days` : ''}
                   end={tr ? (tr.count ? `${tr.count} · ${fmtBytes(tr.bytes)}` : 'empty') : '…'}/>
              {shares === null ? null : mine.length === 0 ? (
                <li className="st-home-quiet">No public links out.</li>
              ) : (
                [...soon, ...mine.filter((x) => !soon.includes(x))].slice(0, 3).map((x) => (
                  <Row key={x.token} title={x.name}
                       onClick={() => (x.kind === 'folder' ? onOpenFolder(x.drive, x.path) : onOpenFile({ id: x.id, drive: x.drive, path: x.path }))}
                       icon={<Icon name="share" size={16}/>}
                       name={x.name}
                       sub={`public · ${x.mode === 'view' ? 'view only' : 'download'} · ${x.hits} visit${x.hits === 1 ? '' : 's'}`}
                       end={x.expires_at ? `ends ${fmtDate(x.expires_at)}` : 'no end'} endWarn={soon.includes(x)}/>
                ))
              )}
              {mine.length > 3 && <li className="st-home-quiet">and {mine.length - 3} more public links</li>}
            </ul>
          </Panel>
        </aside>
      </div>

      {naming && target && (
        <NameModal title={`New folder on ${label(target)}`} label="Folder name" confirmLabel="Create"
                   onClose={() => setNaming(false)}
                   onSubmit={async (name) => {
                     const f = await api.createFolder(token, target, '/', name);
                     toast?.('ok', 'Folder made', `${label(target)} /${f.name}`);
                     onChanged?.();
                     onOpenFolder(target, f.path);
                   }}/>
      )}
    </div>
  );
}
